<template>
  <section
    id="viewport-banner"
    class="banner"
  >
    <BannerController
      v-for="banner in filteredBanners"
      :key="banner.name"
      :bannerContent="banner"
      @click="() => trackBannerClick(banner)"
    />
  </section>
</template>

<script>
import {
  ItemTypeConsts,
  StoreModelMap,
} from 'client/util/Constants';
import { get } from 'lodash';
import { mapActions, mapGetters, mapState } from 'vuex';
import AccountConstants from 'client/util/Constants/AccountConstants';
import AnnouncementBannerDisplayMixin from 'client/GrowthEngineering/utilities/AnnouncementBannerDisplayMixin';
import BannerController from 'client/components/ViewportBanner/BannerController.vue';
import CreateBanners from 'client/components/ViewportBanner/CreateBanners';
import DirectoryApi from 'client/api/v2/DirectoryApi';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import MspState from 'client/components/models/MspState';
import RadiusServerApi from 'client/api/v2/RadiusServerApi';
import SystemApi from 'client/api/v2/SystemApi';

const { isAnMsp } = MspState;

const { accountsReceivable } = AccountConstants;
const mdmSettingsModel = StoreModelMap[ItemTypeConsts.mdmSettings];

export default {
  name: 'ViewportBanner',

  components: {
    BannerController,
  },

  mixins: [AnnouncementBannerDisplayMixin],

  data() {
    return {
      banners: [],
    };
  },

  computed: {
    ...mapGetters('SettingsModule', ['isCommandRunner']),
    ...mapGetters('OrganizationsCollection', {
      getOrganizationDetails: 'getItemDetails',
      orgCreatedDate: 'getOrgCreatedDate',
      organizations: 'getCollection',
    }),
    ...mapState('SettingsModule', ['isCustomer', 'orgId', 'disallowOrgCreation']),

    accountsReceivable() {
      return get(this.getOrganizationDetails(this.orgId), 'accountsReceivable', accountsReceivable.NONE);
    },

    filteredBanners() {
      let applicableBanners = [];
      if (this.shouldShowForAdmin) {
        // only show newlySelfServedBanner if in showBanner list
        applicableBanners = this.banners.filter(banner => banner.showBanner && banner.name === 'newlySelfServedBanner');
        applicableBanners = !applicableBanners.length
          ? this.banners.filter(banner => banner.showBanner) : applicableBanners;
      }
      return applicableBanners;
    },

    logoUrl() {
      return get(this.organization, 'logoUrl', '');
    },

    organization() {
      return this.organizations.find(organization => organization.id === this.orgId);
    },
  },

  watch: {
    accountsReceivable(newValue) {
      const pastDueBannerShown = this.banners.some(banner => banner.showBanner && banner.name === 'pastDueAccountWarningBanner');
      if (newValue !== accountsReceivable.BANNER && pastDueBannerShown) {
        // refresh banners if past due account banner is visible and accounts receivable is updated
        this.createBanners();
      }
    },
  },

  DirectoryApi,
  EventHelper,
  SystemApi,
  RadiusServerApi,

  async created() {
    await this.createBanners();
  },

  methods: {
    ...mapGetters(mdmSettingsModel, ['getDepTokenState']),
    ...mapActions(mdmSettingsModel, ['fetchMdmSettings']),

    async createBanners() {
      const user = await this.getUserConfiguration();
      const provider = await this.getProviderConfiguration();
      this.banners = CreateBanners({ user, provider });
    },

    getDeviceCount() {
      return this.$options.SystemApi.fetchTotalCount();
    },

    async getProviderConfiguration() {
      return { disallowOrgCreation: this.disallowOrgCreation };
    },

    getRadiusServerCount() {
      return this.$options.RadiusServerApi.fetchTotalCount();
    },

    async getUserConfiguration() {
      let results = [];
      try {
        results = await Promise.all([
          this.getDeviceCount(),
          this.getRadiusServerCount(),
          this.fetchMdmSettings(),
        ]);
      } catch {
        // NOOP
      }

      const [deviceCount, radiusServerCount] = results;

      const mdmDepServerTokenState = await this.getDepTokenState();

      return {
        accountsReceivable: this.accountsReceivable,
        isCommandRunner: this.isCommandRunner,
        isAnMsp,
        isCustomer: this.isCustomer,
        hasOrgLogo: !!this.logoUrl,
        orgCreatedDate: this.orgCreatedDate,
        deviceCount,
        radiusServerCount,
        mdmDepServerTokenState,
      };
    },

    trackBannerClick(banner) {
      const { learnMoreLink, learnMoreText, name } = banner;
      this.$options.EventHelper.triggerBannerLinkClicked({
        learnMoreLink,
        learnMoreText,
        name,
      });
    },
  },
};
</script>

<style module>
.container {
  position: relative;
}
</style>
